import React from 'react';
import { formatLogTimestamp } from '../utils/dateUtils';

/**
 * Reusable SMS Consent Checkbox Component
 * Shows SMS consent checkbox with proper legal language for Twilio compliance
 *
 * @param {boolean} checked - Whether the checkbox is checked
 * @param {function} onChange - Callback when checkbox changes (receives boolean)
 * @param {string} consentTimestamp - ISO timestamp when consent was given (optional)
 * @param {boolean} disabled - Whether checkbox is disabled (optional)
 * @param {string} context - Context where checkbox is shown: 'registration', 'profile', or 'notifications' (optional)
 */
export default function SmsConsentCheckbox({
  checked = false,
  onChange,
  consentTimestamp = null,
  disabled = false,
  context = 'profile',
  timezone
}) {
  const getContextualMessage = () => {
    switch (context) {
      case 'registration':
        return 'By checking this box, you consent to receive SMS messages from Brings to Mind. This is optional — we will verify your account by email regardless.';
      case 'notifications':
        return 'Enable SMS reflection reminders in addition to account security messages. You must have SMS consent enabled in your Profile to use this feature.';
      default:
        return 'By checking this box, you consent to receive SMS messages from Brings to Mind.';
    }
  };

  return (
    <div style={{
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: 'var(--color-bg-light)',
      borderRadius: '8px',
      border: '1px solid var(--color-border)'
    }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          style={{ marginTop: '0.25rem', cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0 }}
        />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text)', lineHeight: '1.5', fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>
            I consent to receive SMS messages from Brings to Mind
          </span>
          <p style={{
            fontSize: '0.8rem',
            color: 'var(--color-text)',
            marginTop: 0,
            marginBottom: '0.5rem',
            lineHeight: '1.5'
          }}>
            {getContextualMessage()}
          </p>
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-light)',
            lineHeight: '1.5',
            marginBottom: '0.5rem'
          }}>
            <p style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              <strong>You will receive:</strong>
            </p>
            <ul style={{ marginTop: 0, marginBottom: '0.5rem', paddingLeft: '1.25rem' }}>
              <li>Verification codes for login and account security</li>
              <li>Phone number change confirmations</li>
              <li>Password reset codes (if you use SMS recovery)</li>
              <li>Reflection reminders (only if you enable them in Notifications)</li>
            </ul>
            <p style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <strong>Message Frequency:</strong> Varies based on your activity and settings. Security messages sent as needed. Reflection reminders are optional and configurable (daily to monthly).
            </p>
            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              <strong>Standard Disclosures:</strong> Message & data rates may apply. Reply <strong>STOP</strong> to opt out anytime. Reply <strong>HELP</strong> for assistance.
            </p>
          </div>
          <p style={{
            fontSize: '0.7rem',
            color: 'var(--color-text-light)',
            marginTop: '0.75rem',
            marginBottom: 0,
            lineHeight: '1.4'
          }}>
            By consenting, you agree to our{' '}
            <a
              href="?page=terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
            >
              Terms of Service
            </a>
            {', '}
            <a
              href="?page=privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            {', and '}
            <a
              href="?page=sms-opt-in"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
            >
              SMS Terms & Conditions
            </a>
            .
          </p>
          {checked && consentTimestamp && (
            <p style={{
              fontSize: '0.7rem',
              color: 'var(--color-success)',
              marginTop: '0.75rem',
              marginBottom: 0,
              fontStyle: 'italic',
              fontWeight: '500'
            }}>
              ✓ SMS consent granted on: {formatLogTimestamp(consentTimestamp, timezone)}
            </p>
          )}
        </div>
      </label>
    </div>
  );
}
